import { supabase } from "../lib/supabase";
import type { Booking, Product } from "../types/booking";
import { InvoiceLine } from "../types/invoice";
import { SupabaseClient } from "@supabase/supabase-js";

// Type cast supabase to avoid TypeScript errors
const typedSupabase = supabase as unknown as SupabaseClient;

interface PricingComponent {
  id: number;
  name: string;
  description: string | null;
  component_type: string;
  calculation_type: string;
}

interface PricingRule {
  id: number;
  name: string;
  valid_from: string;
  valid_to: string | null;
  transport_type: number | null;
  origin_port: number | null;
  destination_port: number | null;
  shipment_type: number | null;
  container_size: number | null;
  incoterm: number | null;
}

interface PricingValue {
  id: number;
  pricing_rule_id: number;
  pricing_component_id: number;
  value: number;
  unit: string | null;
  currency: string;
  included_products: number | null;
  extra_product_cost: number | null;
  pricing_component?: PricingComponent;
}

interface CompanyPricingOverride {
  id: number;
  company_id: number;
  pricing_rule_id: number;
  pricing_component_id: number;
  override_type: 'fixed_price' | 'percentage_adjustment' | 'disabled';
  fixed_value?: number;
  fixed_currency?: string;
  fixed_included_products?: number;
  fixed_extra_product_cost?: number;
  percentage_adjustment?: number;
  is_active: boolean;
  valid_from?: string;
  valid_to?: string;
}

/**
 * Find matching pricing rules for a booking
 * @param booking The booking to find matching rules for
 * @param matchDate Optional date to use for matching rules (e.g., container ETD date)
 */
export const findMatchingPricingRules = async (booking: Booking, matchDate?: string | null) => {
  try {
    // Use provided matchDate if available, otherwise use today
    const dateToMatch = matchDate || new Date().toISOString().split('T')[0];

    console.log(`Finding pricing rules for date: ${dateToMatch}`);

    // Get container origin and destination ports if available
    let originPort = booking.origin_port;
    let destinationPort = booking.destination_port;

    // Check for container ports to override booking ports
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;
      
      // If container has origin port, use that instead of booking origin port
      if (containerInfo.origin_port) {
        const containerOriginPort = Array.isArray(containerInfo.origin_port)
          ? containerInfo.origin_port[0]
          : containerInfo.origin_port;
        
        if (containerOriginPort && containerOriginPort.id) {
          console.log(`Using container origin port ${containerOriginPort.id} instead of booking origin port ${booking.origin_port}`);
          originPort = containerOriginPort.id;
        }
      }
      
      // If container has destination port, use that instead of booking destination port
      if (containerInfo.destination_port) {
        const containerDestPort = Array.isArray(containerInfo.destination_port)
          ? containerInfo.destination_port[0]
          : containerInfo.destination_port;
        
        if (containerDestPort && containerDestPort.id) {
          console.log(`Using container destination port ${containerDestPort.id} instead of booking destination port ${booking.destination_port}`);
          destinationPort = containerDestPort.id;
        }
      }
    }

    // Query for matching rules
    const { data, error } = await typedSupabase
      .from("pricing_rules")
      .select("*")
      .lte("valid_from", dateToMatch)
      .or(`valid_to.gte.${dateToMatch},valid_to.is.null`)
      .eq("is_active", true);

    if (error) throw error;

    if (!data || data.length === 0) {
      return { rules: [], error: null, allRules: [], rejectedRules: [] };
    }

    // Helper function to calculate rule specificity (higher = more specific)
    const calculateSpecificity = (rule: PricingRule): number => {
      let specificity = 0;
      if (rule.origin_port !== null) specificity++;
      if (rule.destination_port !== null) specificity++;
      if (rule.transport_type !== null) specificity++;
      if (rule.shipment_type !== null) specificity++;
      if (rule.container_size !== null) specificity++;
      if (rule.incoterm !== null) specificity++;
      return specificity;
    };

    // Helper function to get rejection reasons
    const getRejectionReasons = (rule: PricingRule): string[] => {
      const reasons: string[] = [];
      
      if (rule.transport_type !== null && rule.transport_type !== booking.transport_type) {
        reasons.push("Transport type mismatch");
      }
      if (rule.origin_port !== null && rule.origin_port !== originPort) {
        reasons.push("Origin port mismatch");
      }
      if (rule.destination_port !== null && rule.destination_port !== destinationPort) {
        reasons.push("Destination port mismatch");
      }
      if (rule.shipment_type !== null && rule.shipment_type !== booking.booking_type) {
        reasons.push("Shipment type mismatch");
      }
      if (rule.incoterm !== null && rule.incoterm !== booking.booking_incoterm) {
        reasons.push("Incoterm mismatch");
      }
      
      return reasons;
    };

    // Separate matching and rejected rules
    const matchingRules: PricingRule[] = [];
    const rejectedRules: Array<{ rule: PricingRule; reasonsRejected: string[] }> = [];

    data.forEach((rule: PricingRule) => {
      const rejectionReasons = getRejectionReasons(rule);
      
      if (rejectionReasons.length === 0) {
        matchingRules.push(rule);
      } else {
        rejectedRules.push({ rule, reasonsRejected: rejectionReasons });
      }
    });

    // Sort matching rules by specificity (most specific first)
    const sortedRules = matchingRules.sort((a, b) => {
      const specificityA = calculateSpecificity(a);
      const specificityB = calculateSpecificity(b);
      return specificityB - specificityA; // Higher specificity first
    });

    return { 
      rules: sortedRules, 
      error: null, 
      allRules: data, 
      rejectedRules 
    };
  } catch (error) {
    console.error("Error finding matching pricing rules:", error);
    return { rules: [], error, allRules: [], rejectedRules: [] };
  }
};

/**
 * Get pricing values for a rule with associated components
 */
export const getPricingValues = async (ruleId: number) => {
  try {
    const { data, error } = await typedSupabase
      .from("pricing_values")
      .select(`
        *,
        pricing_component:pricing_components(*)
      `)
      .eq("pricing_rule_id", ruleId);

    if (error) throw error;

    return { values: data || [], error: null };
  } catch (error) {
    console.error("Error fetching pricing values:", error);
    return { values: [], error };
  }
};

/**
 * Get all pricing components grouped by type
 */
export const getPricingComponentsGrouped = async () => {
  try {
    const { data, error } = await typedSupabase
      .from("pricing_components")
      .select("*")
      .order("name");

    if (error) throw error;

    // Group by component_type
    const grouped: Record<string, PricingComponent[]> = {};
    
    (data || []).forEach((component: PricingComponent) => {
      if (!grouped[component.component_type]) {
        grouped[component.component_type] = [];
      }
      grouped[component.component_type].push(component);
    });

    return { components: grouped, error: null };
  } catch (error) {
    console.error("Error fetching pricing components:", error);
    return { components: {}, error };
  }
};

/**
 * Get company pricing overrides for specific company and pricing rules
 */
export const getCompanyPricingOverrides = async (companyId: number, ruleIds: number[]) => {
  try {
    if (ruleIds.length === 0) {
      return { overrides: [], error: null };
    }

    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await typedSupabase
      .from("company_pricing_overrides")
      .select("*")
      .eq("company_id", companyId)
      .in("pricing_rule_id", ruleIds)
      .eq("is_active", true)
      .or(`valid_from.is.null,valid_from.lte.${today}`)
      .or(`valid_to.is.null,valid_to.gte.${today}`);

    if (error) throw error;

    return { overrides: data || [], error: null };
  } catch (error) {
    console.error("Error fetching company pricing overrides:", error);
    return { overrides: [], error };
  }
};

/**
 * Apply company pricing override to a pricing value
 */
const applyCompanyOverride = (
  baseValue: PricingValue, 
  override?: CompanyPricingOverride
): PricingValue | null => {
  if (!override || !override.is_active) {
    return baseValue;
  }

  switch (override.override_type) {
    case 'fixed_price':
      return {
        ...baseValue,
        value: override.fixed_value ?? baseValue.value,
        included_products: override.fixed_included_products ?? baseValue.included_products,
        extra_product_cost: override.fixed_extra_product_cost ?? baseValue.extra_product_cost,
        currency: override.fixed_currency || baseValue.currency
      };
      
    case 'percentage_adjustment': {
      const adjustment = override.percentage_adjustment || 1;
      return {
        ...baseValue,
        value: baseValue.value * adjustment,
        extra_product_cost: baseValue.extra_product_cost 
          ? baseValue.extra_product_cost * adjustment 
          : baseValue.extra_product_cost
      };
    }
      
    case 'disabled':
      return null; // Component will be skipped
      
    default:
      return baseValue;
  }
};

/**
 * Generate invoice lines from matching pricing rules
 */
export const generateInvoiceLinesFromPricing = async (
  booking: Booking, 
  companyId?: number
): Promise<{
  lines: InvoiceLine[];
  currency: string;
  error: unknown;
  ruleSelectionInfo?: {
    selectedRule: PricingRule | null;
    totalRulesFound: number;
    specificityScore: number;
    matchingCriteria: string[];
    rejectedRules: Array<{
      rule: PricingRule;
      reasonsRejected: string[];
    }>;
  };
}> => {
  try {
    // Extract container ETD date if available
    let containerEtdDate = null;
    if (booking.container_info) {
      const containerInfo = Array.isArray(booking.container_info)
        ? booking.container_info[0]
        : booking.container_info;
      
      if (containerInfo && containerInfo.etd) {
        containerEtdDate = containerInfo.etd;
        console.log(`Using container ETD date for pricing: ${containerEtdDate}`);
      }
    }
    
    // Find matching pricing rules using container ETD date if available
    const { rules, error: rulesError, allRules, rejectedRules } = await findMatchingPricingRules(booking, containerEtdDate);
    
    if (rulesError) throw rulesError;
    
    if (rules.length === 0) {
      return { 
        lines: [], 
        currency: "EUR", 
        error: "No matching pricing rules found",
        ruleSelectionInfo: {
          selectedRule: null,
          totalRulesFound: allRules?.length || 0,
          specificityScore: 0,
          matchingCriteria: [],
          rejectedRules: rejectedRules || []
        }
      };
    }

    // Get best matching rule (highest specificity)
    const bestRule = rules[0];

    // Build matching criteria description
    const matchingCriteria: string[] = [];
    if (bestRule.origin_port !== null) matchingCriteria.push("Specific Origin Port");
    if (bestRule.destination_port !== null) matchingCriteria.push("Specific Destination Port");
    if (bestRule.transport_type !== null) matchingCriteria.push("Specific Transport Type");
    if (bestRule.shipment_type !== null) matchingCriteria.push("Specific Shipment Type");
    if (bestRule.container_size !== null) matchingCriteria.push("Specific Container Size");
    if (bestRule.incoterm !== null) matchingCriteria.push("Specific Incoterm");
    if (matchingCriteria.length === 0) matchingCriteria.push("General Rule (matches all)");
    
    // Get pricing values for the rule
    const { values, error: valuesError } = await getPricingValues(bestRule.id);
    
    if (valuesError) throw valuesError;
    
    // Get company overrides if companyId is provided
    let companyOverrides: CompanyPricingOverride[] = [];
    if (companyId) {
      const { overrides, error: overridesError } = await getCompanyPricingOverrides(
        companyId, 
        [bestRule.id]
      );
      if (overridesError) {
        console.warn("Failed to fetch company overrides:", overridesError);
      } else {
        companyOverrides = overrides;
      }
    }
    
    // Apply company overrides
    const appliedValues = values.map((value: PricingValue) => {
      const override = companyOverrides.find((o: CompanyPricingOverride) => 
        o.pricing_rule_id === value.pricing_rule_id && 
        o.pricing_component_id === value.pricing_component_id
      );
      return applyCompanyOverride(value, override);
    });
    
    // Filter out null values (disabled components)
    const filteredValues = appliedValues.filter((value): value is PricingValue => value !== null);
    
    // Generate invoice lines from pricing values
    const lines: InvoiceLine[] = filteredValues.map((value: PricingValue) => {
      const component = value.pricing_component;
      
      if (!component) {
        return {
          description: `Unknown component (ID: ${value.pricing_component_id})`,
          quantity: 1,
          unit_price: value.value,
          vat_percentage: 21,
          cost_type: value.pricing_component_id.toString()
        };
      }
      
      // Calculate quantity based on component calculation type
      let quantity = 1;
      let unitPrice = value.value;
      
      // Process based on calculation type
      let totalWeight: number;
      let totalVolume: number;
      let productCount: number;
      let totalCartons: number;
      
      switch (component.calculation_type) {
        case "per_kg":
          // Get total weight from booking
          totalWeight = getTotalWeightFromBooking(booking);
          quantity = totalWeight;
          break;
        case "per_cbm":
          // Get total volume from booking
          totalVolume = getTotalVolumeFromBooking(booking);
          quantity = totalVolume;
          break;
        case "per_product":
          // Get total products count
          productCount = getTotalProductsFromBooking(booking);
          // Handle included products logic
          if (value.included_products && value.included_products > 0 && value.extra_product_cost !== null) {
            if (productCount <= value.included_products) {
              // All products are included in base price
              quantity = 1;
              unitPrice = value.value;
            } else {
              // Calculate total cost: base price + extra products * extra cost
              const extraProducts = productCount - value.included_products;
              quantity = 1;
              unitPrice = value.value + (extraProducts * value.extra_product_cost);
            }
          } else {
            quantity = productCount;
          }
          break;
        case "per_unique_product":
          // Get unique products count based on HS codes
          productCount = getTotalUniqueProductsFromBooking(booking);
          // Handle included products logic
          if (value.included_products && value.included_products > 0 && value.extra_product_cost !== null) {
            if (productCount <= value.included_products) {
              // All unique products are included in base price
              quantity = 1;
              unitPrice = value.value;
            } else {
              // Calculate total cost: base price + extra unique products * extra cost
              const extraProducts = productCount - value.included_products;
              quantity = 1;
              unitPrice = value.value + (extraProducts * value.extra_product_cost);
            }
          } else {
            quantity = productCount;
          }
          break;
        case "per_carton":
          // Get total cartons count
          totalCartons = getTotalCartonsFromBooking(booking);
          quantity = totalCartons;
          break;
        case "per_pallet":
          // Get total cartons and calculate pallets (8 cartons per pallet assumption)
          totalCartons = getTotalCartonsFromBooking(booking);
          quantity = Math.ceil(totalCartons / 8); // Round up to nearest pallet
          break;
        case "per_shipment":
          // Fixed cost per shipment
          quantity = 1;
          break;
        default:
          // Default to fixed amount
          quantity = 1;
          unitPrice = value.value;
      }
      
      // Generate description with calculation breakdown for tiered pricing
      let description = component.description || component.name;
      
      // Add calculation breakdown for per_product and per_unique_product with tiered pricing
      if ((component.calculation_type === "per_product" || component.calculation_type === "per_unique_product") && 
          value.included_products && value.included_products > 0 && value.extra_product_cost !== null) {
        
        const totalCount = component.calculation_type === "per_unique_product" ? 
          getTotalUniqueProductsFromBooking(booking) : 
          getTotalProductsFromBooking(booking);
        
        if (totalCount > value.included_products) {
          const extraCount = totalCount - value.included_products;
          if (component.calculation_type === "per_unique_product") {
            const extraText = extraCount === 1 ? "unieke HS code" : "unieke HS codes";
            description += ` (${value.included_products} HS codes inbegrepen + ${extraCount} ${extraText} extra)`;
          } else {
            const extraText = extraCount === 1 ? "product" : "producten";
            description += ` (${value.included_products} producten inbegrepen + ${extraCount} ${extraText} extra)`;
          }
        } else {
          if (component.calculation_type === "per_unique_product") {
            const includedText = totalCount === 1 ? "HS code" : "HS codes";
            description += ` (${totalCount} ${includedText} inbegrepen)`;
          } else {
            const includedText = totalCount === 1 ? "product" : "producten";
            description += ` (${totalCount} ${includedText} inbegrepen)`;
          }
        }
      }
      
      return {
        description,
        quantity,
        unit_price: unitPrice,
        vat_percentage: 21, // Default VAT rate
        cost_type: value.pricing_component_id.toString()
      };
    });
    
    // Automatic tariff line addition has been disabled
    // Users can manually add tariff lines using the dedicated tariff calculation section
    
    // Get the currency from the first pricing value or default to EUR
    const currency = filteredValues.length > 0 ? filteredValues[0].currency : "EUR";
    
    // Calculate specificity of the selected rule
    const calculateSpecificityLocal = (rule: PricingRule): number => {
      let specificity = 0;
      if (rule.origin_port !== null) specificity++;
      if (rule.destination_port !== null) specificity++;
      if (rule.transport_type !== null) specificity++;
      if (rule.shipment_type !== null) specificity++;
      if (rule.container_size !== null) specificity++;
      if (rule.incoterm !== null) specificity++;
      return specificity;
    };
    
    return { 
      lines, 
      currency, 
      error: null,
      ruleSelectionInfo: {
        selectedRule: bestRule,
        totalRulesFound: allRules?.length || 0,
        specificityScore: calculateSpecificityLocal(bestRule),
        matchingCriteria,
        rejectedRules: rejectedRules || []
      }
    };
  } catch (error) {
    console.error("Error generating invoice lines from pricing:", error);
    return { lines: [], currency: "EUR", error };
  }
};

// Helper functions to extract booking data
const getTotalWeightFromBooking = (booking: Booking): number => {
  if (!booking.product_info) return 0;
  
  const products = Array.isArray(booking.product_info) ? booking.product_info : [booking.product_info];
  
  return products.reduce((total, product) => {
    if (Array.isArray(product)) {
      return total + product.reduce((sum, p) => sum + (p.gross_weight || 0), 0);
    }
    return total + (product.gross_weight || 0);
  }, 0);
};

const getTotalVolumeFromBooking = (booking: Booking): number => {
  if (!booking.product_info) return 0;
  
  const products = Array.isArray(booking.product_info) ? booking.product_info : [booking.product_info];
  
  return products.reduce((total, product) => {
    if (Array.isArray(product)) {
      return total + product.reduce((sum, p) => sum + (p.cbm || 0), 0);
    }
    return total + (product.cbm || 0);
  }, 0);
};

const getTotalProductsFromBooking = (booking: Booking): number => {
  if (!booking.product_info) return 0;
  
  // Handle different structures of product_info
  if (Array.isArray(booking.product_info)) {
    // If it's already an array of products, return the length
    // This counts each unique product as 1, not the pieces value
    return booking.product_info.length;
  }
  
  // If it's a single product, return 1
  return 1;
};

const getTotalCartonsFromBooking = (booking: Booking): number => {
  if (!booking.product_info) return 0;
  
  const products = Array.isArray(booking.product_info) ? booking.product_info : [booking.product_info];
  
  return products.reduce((total, product) => {
    if (Array.isArray(product)) {
      return total + product.reduce((sum, p) => sum + (p.cartons || 0), 0);
    }
    return total + (product.cartons || 0);
  }, 0);
};

const getTotalUniqueProductsFromBooking = (booking: Booking): number => {
  if (!booking.product_info) return 0;
  
  const products = Array.isArray(booking.product_info) ? booking.product_info : [booking.product_info];
  
  // Get all unique HS codes from products + count products without HS codes as unique
  const uniqueHsCodes = new Set<string>();
  let emptyHsCodeCount = 0;
  
  const processProducts = (productArray: Product[]) => {
    productArray.forEach((product: Product) => {
      if (product.hs_code && product.hs_code.trim()) {
        uniqueHsCodes.add(product.hs_code.trim());
      } else {
        // Each product without HS code counts as unique
        emptyHsCodeCount++;
      }
    });
  };
  
  products.forEach((product: Product | Product[]) => {
    if (Array.isArray(product)) {
      processProducts(product);
    } else if (product) {
      if (product.hs_code && product.hs_code.trim()) {
        uniqueHsCodes.add(product.hs_code.trim());
      } else {
        // Each product without HS code counts as unique
        emptyHsCodeCount++;
      }
    }
  });
  
  // Return total: unique HS codes + products without HS codes
  return uniqueHsCodes.size + emptyHsCodeCount;
}; 